'use client';

import { useEffect, useMemo } from 'react';
import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { RelayType } from '@diritto/common/content/constants';
import { TrackerExtraInfo, TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { FancyShareItem } from '@/app-components/fancyShare/FancyShareItem';
import { useTrackingState } from '@/contexts/TrackingProvider';
import { useGetContestFancySharesQuery } from '@/queries/contests/useGetContestFancySharesQuery';
import { EmptyList } from '@/ui-components/common/EmptyList';
import { Loading } from '@/ui-components/common/Loading';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';

type ContentPageProps = {
  contestId: number;
  isApp?: boolean;
};

export const SectionPage = ({ contestId, isApp }: ContentPageProps) => {
  useInAppHeader({ screen: WebViewScreen.Section, title: '실시간 이멋공' });
  useInAppStatusBar(WebViewScreen.Section);

  return (
    <MutableHeaderLayout title={'실시간 이멋공'} isApp={isApp}>
      <SectionFancyShareList contestId={contestId} />
    </MutableHeaderLayout>
  );
};

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.Section,
};

export const SectionFancyShareList = ({ contestId }: { contestId: number }) => {
  const { data, isPending } = useGetContestFancySharesQuery(contestId);

  const { fancyShares, profiles, contents, episodes } = useMemo(
    () => ({
      fancyShares: data?.fancyShares ?? [],
      profiles: data?.profiles ?? {},
      contents: data?.contents ?? {},
      episodes: data?.episodes ?? {},
    }),
    [data],
  );

  const { tracker } = useTrackingState();
  useEffect(() => {
    tracker.track(TrackingEventType.ViewFancyShareList, { trackerExtraInfo });
  }, [tracker]);

  return (
    <>
      <div className="flex flex-col gap-4 p-4">
        {!isPending && fancyShares.length === 0 && <EmptyList description="이멋공이 없어요." />}
        <div className="flex flex-col gap-4 p-4 items-center">
          {fancyShares.map(fancyShare => {
            const profile = profiles[fancyShare.profileId];
            const content = contents[fancyShare.contentId];
            const episode = episodes[fancyShare.episodeId];
            const authorProfile =
              content && content.relayType === RelayType.Public && content.profileId
                ? profiles[content.profileId]
                : undefined;

            return (
              <FancyShareItem
                key={fancyShare.id}
                fancyShare={fancyShare}
                isLiked={false}
                profile={profile}
                authorProfile={authorProfile}
                episode={episode?.relayType === RelayType.Public ? episode : undefined}
                content={content?.relayType === RelayType.Public ? content : undefined}
                trackerExtraInfo={trackerExtraInfo}
                onDispatchAction={() => {}}
                showMenuButton={false}
                onClick={() => tracker.track(TrackingEventType.ViewFancyShareList, { trackerExtraInfo })}
              />
            );
          })}
          {isPending && <Loading />}
        </div>
      </div>
    </>
  );
};
